import { useEffect, useMemo, useState } from "react";
import { CDSButton } from "@ciscodesignsystems/cds-react-button";
import { CDSCard } from "@ciscodesignsystems/cds-react-card";
import { CDSCheckbox } from "@ciscodesignsystems/cds-react-checkbox";
import { CDSCollapse } from "@ciscodesignsystems/cds-react-collapse";
import { CDSContainer } from "@ciscodesignsystems/cds-react-container";
import { CDSFlex } from "@ciscodesignsystems/cds-react-flex";
import { CDSModal } from "@ciscodesignsystems/cds-react-modal";
import { CDSNotification } from "@ciscodesignsystems/cds-react-notification";
import { CDSRadio } from "@ciscodesignsystems/cds-react-radio";
import { CDSTag } from "@ciscodesignsystems/cds-react-tag";
import { CDSTextArea } from "@ciscodesignsystems/cds-react-textarea";
import { CDSTextInput } from "@ciscodesignsystems/cds-react-text-input";
import { Sparkle } from "phosphor-react";
import YAML from "yaml";
import { generateSpecDraft } from "../services/specGenerationService";

const methodOptions = ["get", "post", "put", "delete"];

const sampleDesign = {
  apiName: "",
  description: "",
  version: "1.0.0",
  serverUrl: "https://api.security.cisco.com",
  basePath: "/v1",
  primaryResources: [
    { name: "organizations", paths: ["/organizations"] },
    { name: "subscriptions", paths: ["/subscriptions"] },
    { name: "users", paths: ["/users"] },
    { name: "roles", paths: ["/roles"] },
    { name: "admin groups", paths: ["/admin-groups"] },
  ],
  authModel: "bearer",
  paginationModel: "cursor",
  includeTrackingHeaders: "yes",
  errorModel: "standard",
};

const emptyDesign = {
  apiName: "",
  description: "",
  version: "",
  serverUrl: "",
  basePath: "",
  primaryResources: [],
  authModel: "",
  paginationModel: "",
  includeTrackingHeaders: "",
  errorModel: "",
};

const emptyResourceDraft = {
  name: "",
  paths: [],
};

const emptyPathDraft = {
  path: "",
  summary: "",
  operationId: "",
  description: "",
  methods: ["get"],
};

const sampleAiPrompt = `Generate an OpenAPI 3.0.3 specification for Product A Service. Include GET /v1/users/{userId} and GET /v1/groups/{groupId} - prompt`;
const designApiStorageKey = "uag.designApiPage";
const AI_SPEC_GENERATION_DELAY_MS = 5000;

const getDefaultPathMethods = () => ["get", "post"];

const normalizeResourceName = (value) => value.trim();

const normalizePath = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const isValidPathParameterName = (value) => {
  if (!value) {
    return false;
  }

  return [...value].every((character) => /\w/.test(character));
};

const inspectPathParameters = (normalizedPath) => {
  const extractedParameters = [];
  let cursor = 0;

  while (cursor < normalizedPath.length) {
    const startIndex = normalizedPath.indexOf("{", cursor);
    const closingBraceIndex = normalizedPath.indexOf("}", cursor);

    if (closingBraceIndex !== -1 && (startIndex === -1 || closingBraceIndex < startIndex)) {
      return {
        extractedParameters,
        unresolvedBraces: true,
      };
    }

    if (startIndex === -1) {
      return {
        extractedParameters,
        unresolvedBraces: false,
      };
    }

    const endIndex = normalizedPath.indexOf("}", startIndex + 1);

    if (endIndex === -1) {
      return {
        extractedParameters,
        unresolvedBraces: true,
      };
    }

    extractedParameters.push(normalizedPath.slice(startIndex + 1, endIndex));
    cursor = endIndex + 1;
  }

  return {
    extractedParameters,
    unresolvedBraces: false,
  };
};

const extractPathParameterNames = (normalizedPath) =>
  inspectPathParameters(normalizedPath).extractedParameters;

const getPathValidationError = (value) => {
  const normalizedPath = normalizePath(value);

  if (!normalizedPath) {
    return "Path is required.";
  }

  if (/\s/.test(normalizedPath)) {
    return "Paths cannot contain spaces.";
  }

  if (normalizedPath.includes("//")) {
    return "Paths cannot contain empty segments.";
  }

  const { extractedParameters, unresolvedBraces } = inspectPathParameters(normalizedPath);
  const hasInvalidParameter = extractedParameters.some(
    (parameterName) => !isValidPathParameterName(parameterName)
  );

  if (hasInvalidParameter) {
    return "Path parameter names can only use letters, numbers, and underscores.";
  }

  if (unresolvedBraces) {
    return "Path parameters must use matching braces, for example /organizations/{orgId}.";
  }

  return "";
};

const toTitleCase = (value) =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join("");

const toYamlScalar = (value) => JSON.stringify(String(value ?? ""));

const toggleMethod = (currentMethods, method, isSelected) => {
  if (isSelected) {
    return [...currentMethods, method];
  }

  return currentMethods.filter((item) => item !== method);
};

const getSuccessCode = (method) => {
  if (method === "post") {
    return "201";
  }

  if (method === "delete") {
    return "204";
  }

  return "200";
};

const getResponseSchema = (method) => {
  if (method === "delete") {
    return "";
  }

  return `          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
`;
};

const getPaginationParameterName = (paginationModel) => {
  if (paginationModel === "cursor") {
    return "cursor";
  }

  return "offset";
};

const getPaginationBlock = (method, paginationModel) => {
  if (method !== "get" || paginationModel === "none") {
    return "";
  }

  return `      parameters:
        - name: ${getPaginationParameterName(paginationModel)}
          in: query
          required: false
          schema:
            type: string
`;
};

const getPathParametersBlock = (path) => {
  const parameterNames = extractPathParameterNames(path);

  if (!parameterNames.length) {
    return "";
  }

  return `${parameterNames
    .map(
      (parameterName) => `        - name: ${toYamlScalar(parameterName)}
          in: path
          required: true
          schema:
            type: string
`
    )
    .join("")}`;
};

const getParametersBlock = (method, paginationModel, path) => {
  const pathParametersBlock = getPathParametersBlock(path);
  const paginationBlock = getPaginationBlock(method, paginationModel);

  if (!pathParametersBlock && !paginationBlock) {
    return "";
  }

  return `      parameters:
${pathParametersBlock}${paginationBlock.replace("      parameters:\n", "")}`;
};

const getRequestBodyBlock = (method) => {
  if (!["post", "put"].includes(method)) {
    return "";
  }

  return `      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
`;
};

const getDescriptionBlock = (description) => {
  const trimmedDescription = description.trim();

  if (!trimmedDescription) {
    return "";
  }

  return `      description: ${toYamlScalar(trimmedDescription)}
`;
};

const getHeadersBlock = (includeTrackingHeaders) => {
  if (includeTrackingHeaders === "yes") {
    return `          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
`;
  }

  return `          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
`;
};

const getErrorResponseBlock = (errorModel) => {
  if (errorModel === "standard") {
    return `        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
`;
  }

  return `        "500":
          description: Internal server error
`;
};

const getOperationId = ({ operationBase, method, methodCount, resourceName }) => {
  if (!operationBase) {
    return `${method}${toTitleCase(resourceName)}`;
  }

  if (methodCount > 1) {
    return `${method}${toTitleCase(operationBase)}`;
  }

  return operationBase;
};

const getSecuritySchemeName = (authModel) => {
  if (authModel === "oauth2") {
    return "oauth2ClientCredentials";
  }

  return "bearerHttpAuthentication";
};

const getSecurityBlock = (authModel) => {
  if (!authModel || authModel === "none") {
    return "";
  }

  return `security:
  - ${getSecuritySchemeName(authModel)}: []
`;
};

const getSecuritySchemesBlock = (authModel, serverUrl) => {
  if (authModel === "bearer") {
    return `  securitySchemes:
    bearerHttpAuthentication:
      type: http
      scheme: bearer
`;
  }

  if (authModel === "oauth2") {
    return `  securitySchemes:
    oauth2ClientCredentials:
      type: oauth2
      flows:
        clientCredentials:
          tokenUrl: ${toYamlScalar(`${serverUrl}/oauth/token`)}
          scopes: {}
`;
  }

  return "";
};

const getTrackingHeaderBlock = (includeTrackingHeaders) => {
  if (includeTrackingHeaders !== "yes") {
    return "";
  }

  return `    TrackingId:
      description: Request correlation identifier
      schema:
        type: string
`;
};

const getErrorComponentsBlock = (errorModel) => {
  if (errorModel !== "standard") {
    return "";
  }

  return `  responses:
    BadRequest:
      description: Invalid request
    Unauthorized:
      description: Unauthorized
    InternalServerError:
      description: Internal server error
`;
};

const normalizePathEntry = (entry) => {
  if (typeof entry === "string") {
    return {
      path: normalizePath(entry),
      summary: "",
      operationId: "",
      description: "",
      methods: getDefaultPathMethods(),
    };
  }

  return {
    path: normalizePath(entry?.path ?? ""),
    summary: (entry?.summary ?? "").trim(),
    operationId: (entry?.operationId ?? "").trim(),
    description: (entry?.description ?? "").trim(),
    methods: (entry?.methods ?? []).filter((method) => methodOptions.includes(method)),
  };
};

const normalizeResource = (resource) => ({
  name: normalizeResourceName(resource.name ?? ""),
  paths: (resource.paths ?? []).map(normalizePathEntry).filter((entry) => entry.path),
});

const parseGeneratedSpecDocument = (specText) => {
  const trimmedSpec = specText.trim();

  if (!trimmedSpec) {
    return null;
  }

  try {
    return YAML.parse(trimmedSpec);
  } catch (yamlError) {
    try {
      return JSON.parse(trimmedSpec);
    } catch (jsonError) {
      return null;
    }
  }
};

const getServerFields = (serverUrlValue) => {
  if (typeof serverUrlValue !== "string" || !serverUrlValue.trim()) {
    return {};
  }

  const trimmedServerUrl = serverUrlValue.trim();

  try {
    const parsedUrl = new URL(trimmedServerUrl);
    const normalizedPath = parsedUrl.pathname === "/" ? "" : parsedUrl.pathname;

    return {
      serverUrl: `${parsedUrl.origin}`,
      basePath: normalizedPath || sampleDesign.basePath,
    };
  } catch (error) {
    if (trimmedServerUrl.startsWith("/")) {
      return {
        serverUrl: "",
        basePath: trimmedServerUrl,
      };
    }

    return {
      serverUrl: trimmedServerUrl,
    };
  }
};

const buildServerUrlFromFormState = (formState, originalServerUrlValue) => {
  const originalServerFields = getServerFields(originalServerUrlValue);

  if (!originalServerFields.serverUrl && originalServerFields.basePath) {
    return formState.basePath;
  }

  return `${formState.serverUrl}${formState.basePath}`;
};

const getAuthModelFromDocument = (document) => {
  const securitySchemes = document?.components?.securitySchemes;
  const schemeEntries =
    securitySchemes && typeof securitySchemes === "object" ? Object.values(securitySchemes) : [];
  const hasOauth2Scheme = schemeEntries.some((scheme) => scheme?.type === "oauth2");
  const hasBearerHttpScheme = schemeEntries.some(
    (scheme) => scheme?.type === "http" && String(scheme?.scheme || "").toLowerCase() === "bearer"
  );

  if (hasOauth2Scheme) {
    return "oauth2";
  }

  if (hasBearerHttpScheme) {
    return "bearer";
  }

  if (Array.isArray(document?.security) && document.security.length === 0) {
    return "none";
  }

  return null;
};

const getFormStateFromGeneratedSpec = (specText) => {
  const document = parseGeneratedSpecDocument(specText);

  if (!document || typeof document !== "object") {
    return null;
  }

  const info = document.info && typeof document.info === "object" ? document.info : {};
  const serverFields = getServerFields(document?.servers?.[0]?.url);
  const authModel = getAuthModelFromDocument(document);
  const nextFormState = {};

  if (typeof info.title === "string") {
    nextFormState.apiName = info.title;
  }

  if (typeof info.description === "string") {
    nextFormState.description = info.description;
  }

  if (typeof info.version === "string") {
    nextFormState.version = info.version;
  }

  if (serverFields.serverUrl) {
    nextFormState.serverUrl = serverFields.serverUrl;
  }

  if (serverFields.basePath) {
    nextFormState.basePath = serverFields.basePath;
  }

  if (authModel) {
    nextFormState.authModel = authModel;
  }

  if (!Object.keys(nextFormState).length) {
    return null;
  }

  return nextFormState;
};

const getPreviewFieldsFromGeneratedDocument = (document) => {
  const info = document?.info && typeof document.info === "object" ? document.info : {};
  const serverFields = getServerFields(document?.servers?.[0]?.url);

  return {
    apiName: typeof info.title === "string" ? info.title : "",
    description: typeof info.description === "string" ? info.description : "",
    version: typeof info.version === "string" ? info.version : "",
    serverUrl: serverFields.serverUrl ?? "",
    basePath: serverFields.basePath ?? "",
  };
};

const areGeneratedPreviewFieldsInSync = (document, formState) => {
  const currentFields = getPreviewFieldsFromGeneratedDocument(document);

  return (
    currentFields.apiName === formState.apiName &&
    currentFields.description === formState.description &&
    currentFields.version === formState.version &&
    currentFields.serverUrl === formState.serverUrl &&
    currentFields.basePath === formState.basePath
  );
};

const syncGeneratedSpecWithFormState = (specText, formState) => {
  const document = parseGeneratedSpecDocument(specText);

  if (!document || typeof document !== "object") {
    return specText;
  }

  if (areGeneratedPreviewFieldsInSync(document, formState)) {
    return specText;
  }

  const nextDocument = JSON.parse(JSON.stringify(document));

  if (!nextDocument.info || typeof nextDocument.info !== "object") {
    nextDocument.info = {};
  }

  nextDocument.info.title = formState.apiName || "API name (title)";
  nextDocument.info.description = formState.description ?? "";
  nextDocument.info.version = formState.version ?? sampleDesign.version;

  const nextServerUrl = buildServerUrlFromFormState(formState, document?.servers?.[0]?.url);

  if (Array.isArray(nextDocument.servers) && nextDocument.servers.length) {
    nextDocument.servers = nextDocument.servers.map((server, index) => {
      if (index !== 0 || !server || typeof server !== "object") {
        return server;
      }

      return {
        ...server,
        url: nextServerUrl,
      };
    });
  } else {
    nextDocument.servers = [{ url: nextServerUrl }];
  }

  return YAML.stringify(nextDocument).trim();
};

const buildMethodBlock = ({
  method,
  resourceName,
  tagName,
  paginationModel,
  includeTrackingHeaders,
  errorModel,
  pathEntry,
}) => {
  const summaryPrefixMap = {
    get: "List",
    post: "Create",
    put: "Update",
    delete: "Delete",
  };
  const summaryPrefix = summaryPrefixMap[method] ?? "Manage";
  const successCode = getSuccessCode(method);
  const responseSchema = getResponseSchema(method);
  const parametersBlock = getParametersBlock(method, paginationModel, pathEntry.path);
  const requestBodyBlock = getRequestBodyBlock(method);
  const headersBlock = getHeadersBlock(includeTrackingHeaders);
  const errorResponseBlock = getErrorResponseBlock(errorModel);

  const summaryText = pathEntry.summary.trim() || `${summaryPrefix} ${resourceName}`;
  const operationBase = pathEntry.operationId.trim();
  const operationId = getOperationId({
    operationBase,
    method,
    methodCount: pathEntry.methods.length,
    resourceName,
  });
  const descriptionBlock = getDescriptionBlock(pathEntry.description);

  return `    ${method}:
      summary: ${toYamlScalar(summaryText)}
${descriptionBlock}      operationId: ${toYamlScalar(operationId)}
      tags:
        - ${toYamlScalar(tagName)}
${parametersBlock}${requestBodyBlock}      responses:
        "${successCode}":
          description: "Successful response"
${headersBlock}${responseSchema}${errorResponseBlock}`;
};

const getMergedPathBlocks = ({
  resources,
  paginationModel,
  includeTrackingHeaders,
  errorModel,
}) => {
  const pathMap = new Map();

  resources.forEach((resource) => {
    resource.paths.forEach((pathEntry) => {
      const selectedMethods = pathEntry.methods.length ? pathEntry.methods : ["get"];
      const existingMethodBlocks = pathMap.get(pathEntry.path) ?? new Map();

      selectedMethods.forEach((method) => {
        existingMethodBlocks.set(
          method,
          buildMethodBlock({
            method,
            resourceName: resource.name,
            tagName: toTitleCase(resource.name),
            paginationModel,
            includeTrackingHeaders,
            errorModel,
            pathEntry,
          })
        );
      });

      pathMap.set(pathEntry.path, existingMethodBlocks);
    });
  });

  if (!pathMap.size) {
    return "  {}";
  }

  return [...pathMap.entries()]
    .map(
      ([path, methodBlocks]) => `  ${toYamlScalar(path)}:
${[...methodBlocks.values()].join("")}`
    )
    .join("\n");
};

const buildSpec = ({
  apiName,
  description,
  version,
  serverUrl,
  basePath,
  primaryResources,
  authModel,
  paginationModel,
  includeTrackingHeaders,
  errorModel,
}) => {
  const resources = primaryResources.map(normalizeResource).filter((resource) => resource.name);
  const tagList = [...new Set(resources.map((resource) => toTitleCase(resource.name)))];
  const specTitle = apiName.trim() || "API name (title)";
  const securityBlock = getSecurityBlock(authModel);
  const pathBlocks = getMergedPathBlocks({
    resources,
    paginationModel,
    includeTrackingHeaders,
    errorModel,
  });
  const trackingHeaderBlock = getTrackingHeaderBlock(includeTrackingHeaders);
  const errorComponentsBlock = getErrorComponentsBlock(errorModel);
  const securitySchemesBlock = getSecuritySchemesBlock(authModel, serverUrl);

  return `openapi: 3.1.0
info:
  title: ${toYamlScalar(specTitle)}
  description: ${toYamlScalar(description)}
  version: ${toYamlScalar(version)}
servers:
  - url: ${toYamlScalar(`${serverUrl}${basePath}`)}
${securityBlock}${tagList.length ? `tags:
${tagList.map((tag) => `  - name: ${toYamlScalar(tag)}`).join("\n")}
` : ""}paths:
${pathBlocks}
components:
  headers:
    ContentType:
      description: Response media type
      schema:
        type: string
${trackingHeaderBlock}${errorComponentsBlock}${securitySchemesBlock}
`;
};

const upsertDraftPath = (paths, editingPathValue, nextEntry) => {
  if (!paths.some((entry) => entry.path === editingPathValue)) {
    return [...paths, nextEntry];
  }

  return paths.map((entry) => (entry.path === editingPathValue ? nextEntry : entry));
};

const removeDraftPath = (paths, pathToRemove) =>
  paths.filter((entry) => entry.path !== pathToRemove);

const upsertPrimaryResource = (primaryResources, editingResourceName, nextResource) => {
  if (!primaryResources.some((resource) => resource.name === editingResourceName)) {
    return [...primaryResources, nextResource];
  }

  return primaryResources.map((resource) =>
    resource.name === editingResourceName ? nextResource : resource
  );
};

const getStoredDesignState = (windowObject) => {
  if (!windowObject) {
    return null;
  }

  try {
    const storedValue = windowObject.localStorage?.getItem(designApiStorageKey);

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);

    if (!parsedValue || typeof parsedValue !== "object") {
      return null;
    }

    return {
      aiGeneratedSpec:
        typeof parsedValue.aiGeneratedSpec === "string" ? parsedValue.aiGeneratedSpec : "",
      aiPrompt: typeof parsedValue.aiPrompt === "string" ? parsedValue.aiPrompt : "",
      formState:
        parsedValue.formState && typeof parsedValue.formState === "object"
          ? { ...emptyDesign, ...parsedValue.formState }
          : emptyDesign,
      isAiPanelExpanded: Boolean(parsedValue.isAiPanelExpanded),
    };
  } catch (error) {
    return null;
  }
};

const getInitialDesignState = () =>
  typeof window === "undefined" ? null : getStoredDesignState(window);

const persistDesignState = (windowObject, state) => {
  if (!windowObject) {
    return;
  }

  try {
    windowObject.localStorage?.setItem(designApiStorageKey, JSON.stringify(state));
  } catch (error) {
    // Ignore persistence failures so the design flow remains usable.
  }
};

const parseDesignStorageEvent = (event) => {
  if (event.key !== designApiStorageKey || !event.newValue) {
    return null;
  }

  try {
    const nextState = JSON.parse(event.newValue);

    return {
      formState:
        nextState.formState && typeof nextState.formState === "object"
          ? { ...emptyDesign, ...nextState.formState }
          : emptyDesign,
      aiPrompt: typeof nextState.aiPrompt === "string" ? nextState.aiPrompt : "",
      aiGeneratedSpec:
        typeof nextState.aiGeneratedSpec === "string" ? nextState.aiGeneratedSpec : "",
      isAiPanelExpanded: Boolean(nextState.isAiPanelExpanded),
    };
  } catch (error) {
    return null;
  }
};

const DesignApiPage = () => {
  const [initialState] = useState(() => getInitialDesignState());
  const [formState, setFormState] = useState(initialState?.formState || sampleDesign);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [isPathModalOpen, setIsPathModalOpen] = useState(false);
  const [resourceDraft, setResourceDraft] = useState(emptyResourceDraft);
  const [resourceDraftError, setResourceDraftError] = useState("");
  const [pathDraft, setPathDraft] = useState(emptyPathDraft);
  const [pathDraftError, setPathDraftError] = useState("");
  const [editingResourceName, setEditingResourceName] = useState("");
  const [editingPathValue, setEditingPathValue] = useState("");
  const [aiPrompt, setAiPrompt] = useState(initialState?.aiPrompt || sampleAiPrompt);
  const [aiGeneratedSpec, setAiGeneratedSpec] = useState(initialState?.aiGeneratedSpec || "");
  const [isGeneratingSpec, setIsGeneratingSpec] = useState(false);
  const [aiErrorMessage, setAiErrorMessage] = useState("");
  const [isAiPanelExpanded, setIsAiPanelExpanded] = useState(
    initialState?.isAiPanelExpanded || false
  );
  const structuredSpec = useMemo(() => buildSpec(formState), [formState]);
  const generatedSpec = useMemo(() => {
    if (!aiGeneratedSpec) {
      return structuredSpec;
    }

    return syncGeneratedSpecWithFormState(aiGeneratedSpec, formState);
  }, [aiGeneratedSpec, formState, structuredSpec]);

  const resourceList = useMemo(
    () => formState.primaryResources.map(normalizeResource).filter((resource) => resource.name),
    [formState.primaryResources]
  );
  const tagList = useMemo(() => resourceList.map((resource) => toTitleCase(resource.name)), [resourceList]);
  const methodCount = useMemo(
    () => new Set(resourceList.flatMap((resource) => resource.paths.flatMap((path) => path.methods))).size,
    [resourceList]
  );
  let authSummaryLabel = "Auth not set";
  if (formState.authModel) {
    authSummaryLabel = formState.authModel === "none" ? "Public" : "Secured";
  }

  let paginationSummaryLabel = "Pagination not set";
  if (formState.paginationModel) {
    paginationSummaryLabel =
      formState.paginationModel === "none"
        ? "No pagination"
        : `${formState.paginationModel} pagination`;
  }

  const handleInputChange = (field) => (event) => {
    setFormState((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleAiPromptChange = (event) => {
    setAiPrompt(event.target.value);
    setAiErrorMessage("");
  };

  const openResourceModal = (resourceToEdit) => {
    if (resourceToEdit?.paths) {
      setResourceDraft({
        name: resourceToEdit.name,
        paths: resourceToEdit.paths.map((pathEntry) => ({
          path: pathEntry.path,
          summary: pathEntry.summary ?? "",
          operationId: pathEntry.operationId ?? "",
          description: pathEntry.description ?? "",
          methods: [...pathEntry.methods],
        })),
      });
      setEditingResourceName(resourceToEdit.name);
    } else {
      setResourceDraft(emptyResourceDraft);
      setEditingResourceName("");
    }
    setResourceDraftError("");
    setIsResourceModalOpen(true);
  };

  const closeResourceModal = () => {
    setResourceDraft(emptyResourceDraft);
    setResourceDraftError("");
    setEditingResourceName("");
    setPathDraft(emptyPathDraft);
    setPathDraftError("");
    setEditingPathValue("");
    setIsPathModalOpen(false);
    setIsResourceModalOpen(false);
  };

  const handleResourceDraftChange = (field) => (event) => {
    setResourceDraft((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    setResourceDraftError("");
  };

  const openPathModal = (pathToEdit) => {
    if (pathToEdit?.path) {
      setPathDraft({
        path: pathToEdit.path,
        summary: pathToEdit.summary ?? "",
        operationId: pathToEdit.operationId ?? "",
        description: pathToEdit.description ?? "",
        methods: [...pathToEdit.methods],
      });
      setEditingPathValue(pathToEdit.path);
    } else {
      setPathDraft(emptyPathDraft);
      setEditingPathValue("");
    }
    setPathDraftError("");
    setIsPathModalOpen(true);
  };

  const closePathModal = () => {
    setPathDraft(emptyPathDraft);
    setPathDraftError("");
    setEditingPathValue("");
    setIsPathModalOpen(false);
  };

  const handlePathDraftChange = (field) => (event) => {
    setPathDraft((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    setPathDraftError("");
  };

  const handleSavePathDraft = () => {
    const nextPath = normalizePath(pathDraft.path);
    const validationError = getPathValidationError(nextPath);
    const alreadyExists = resourceDraft.paths.some(
      (entry) =>
        entry.path.toLowerCase() === nextPath.toLowerCase() &&
        entry.path.toLowerCase() !== editingPathValue.toLowerCase()
    );

    if (validationError) {
      setPathDraftError(validationError);
      return;
    }

    if (alreadyExists) {
      setPathDraftError("Paths must be unique for each resource.");
      return;
    }

    if (!pathDraft.methods.length) {
      setPathDraftError("Select at least one method for this path.");
      return;
    }

    const nextEntry = {
      path: nextPath,
      summary: pathDraft.summary.trim(),
      operationId: pathDraft.operationId.trim(),
      description: pathDraft.description.trim(),
      methods: [...pathDraft.methods],
    };

    setResourceDraft((current) => ({
      ...current,
      paths: upsertDraftPath(current.paths, editingPathValue, nextEntry),
    }));
    setResourceDraftError("");
    closePathModal();
  };

  const handleRemoveDraftPath = (pathToRemove) => {
    setResourceDraft((current) => ({
      ...current,
      paths: removeDraftPath(current.paths, pathToRemove),
    }));
    setResourceDraftError("");
  };

  const handleSaveResource = () => {
    const nextName = normalizeResourceName(resourceDraft.name);
    const alreadyExists = resourceList.some(
      (resource) =>
        resource.name.toLowerCase() === nextName.toLowerCase() &&
        resource.name.toLowerCase() !== editingResourceName.toLowerCase()
    );

    if (!nextName) {
      setResourceDraftError("Resource name is required.");
      return;
    }

    if (alreadyExists) {
      setResourceDraftError("Primary resources must be unique.");
      return;
    }

    if (!resourceDraft.paths.length) {
      setResourceDraftError("Add at least one path for this resource.");
      return;
    }

    setFormState((current) => ({
      ...current,
      primaryResources: upsertPrimaryResource(current.primaryResources, editingResourceName, {
        name: nextName,
        paths: resourceDraft.paths,
      }),
    }));
    closeResourceModal();
  };

  const handleRemoveResource = (resourceToRemove) => {
    setFormState((current) => ({
      ...current,
      primaryResources: current.primaryResources.filter(
        (resource) => resource.name !== resourceToRemove
      ),
    }));
  };

  const handleDraftMethodChange = (method) => (event) => {
    setPathDraft((current) => ({
      ...current,
      methods: toggleMethod(current.methods, method, event.target.checked),
    }));
    setPathDraftError("");
  };

  const handleReset = () => {
    setFormState(emptyDesign);
    setAiGeneratedSpec("");
    setAiErrorMessage("");
    setAiPrompt("");
    setIsAiPanelExpanded(false);
    closeResourceModal();
  };

  const handleDownload = () => {
    const blob = new Blob([generatedSpec], { type: "application/yaml;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = "generated-openapi.yaml";
    link.click();
    URL.revokeObjectURL(href);
  };

  const handleGenerateWithAi = async () => {
    const normalizedPrompt = aiPrompt.trim();

    if (!normalizedPrompt) {
      setAiErrorMessage("Add a prompt before generating an OpenAPI draft.");
      return;
    }

    setIsGeneratingSpec(true);
    setAiErrorMessage("");

    try {
      await new Promise((resolve) => {
        window.setTimeout(resolve, AI_SPEC_GENERATION_DELAY_MS);
      });

      const nextSpec = await generateSpecDraft({
        prompt: normalizedPrompt,
      });
      const nextFormFields = getFormStateFromGeneratedSpec(nextSpec);

      setAiGeneratedSpec(nextSpec);
      if (nextFormFields) {
        setFormState((current) => ({
          ...current,
          ...nextFormFields,
        }));
      }
    } catch (error) {
      setAiErrorMessage(error.message || "Unable to generate the OpenAPI draft.");
    } finally {
      setIsGeneratingSpec(false);
    }
  };

  const handleRemoveAiDraft = () => {
    setAiGeneratedSpec("");
    setAiErrorMessage("");
  };

  useEffect(() => {
    persistDesignState(typeof window === "undefined" ? null : window, {
      aiGeneratedSpec,
      aiPrompt,
      formState,
      isAiPanelExpanded,
    });
    return undefined;
  }, [aiGeneratedSpec, aiPrompt, formState, isAiPanelExpanded]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      const nextState = parseDesignStorageEvent(event);

      if (!nextState) {
        return;
      }

      setFormState(nextState.formState);
      setAiPrompt(nextState.aiPrompt);
      setAiGeneratedSpec(nextState.aiGeneratedSpec);
      setIsAiPanelExpanded(nextState.isAiPanelExpanded);
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <section className="uag-design-page">
      <CDSFlex direction="vertical" gap={16}>
        <div className="uag-page-heading">
          <h1>Design API</h1>
          <p>
            Design an API or a set of APIs using an AI prompt, or manually enter API details to
            form the specification.
          </p>
        </div>

        <div className="uag-design-grid">
          <CDSFlex direction="vertical" gap={16} className="uag-design-input-column">
            <CDSCard className="uag-ai-design-card">
              <CDSFlex direction="vertical" gap={16}>
                <div className="uag-ai-design-header">
                  <div className="uag-ai-design-badge" aria-hidden="true">
                    <Sparkle size={20} weight="fill" />
                  </div>
                  <div>
                    <h2>AI Design</h2>
                    <p className="uag-section-copy">
                      Optional prompt-driven draft generation for teams who want to start from free
                      text before refining the manual spec builder.
                    </p>
                  </div>
                </div>

                <CDSCollapse
                  className="uag-ai-design-collapse"
                  open={isAiPanelExpanded}
                  onChange={setIsAiPanelExpanded}
                >
                  <CDSCollapse.Heading>AI Design prompt</CDSCollapse.Heading>
                  <CDSCollapse.Panel>
                    <div className="uag-ai-design-panel">
                      <div>
                        <h2>Prompt input</h2>
                        <p className="uag-section-copy">
                          Describe the service, consumers, auth model, pagination style, and explicit
                          API routes.
                        </p>
                      </div>
                      <CDSTextArea
                        id="design-ai-prompt"
                        aria-label="AI Design prompt"
                        disabled={isGeneratingSpec}
                        value={aiPrompt}
                        onChange={handleAiPromptChange}
                        placeholder="Describe the API you want the service to generate."
                        rows={8}
                      />
                      <CDSFlex gap={8} wrap className="uag-ai-design-actions">
                        <CDSButton onClick={handleGenerateWithAi} disabled={isGeneratingSpec}>
                          {isGeneratingSpec ? "Generating..." : "Generate AI spec"}
                        </CDSButton>
                        {aiGeneratedSpec ? (
                          <CDSButton variant="tertiary" onClick={handleRemoveAiDraft}>
                            Remove AI draft
                          </CDSButton>
                        ) : null}
                      </CDSFlex>
                      {aiErrorMessage ? (
                        <CDSNotification
                          className="uag-ai-design-notification"
                          title="Draft generation failed"
                          description={aiErrorMessage}
                          status="negative"
                        />
                      ) : null}
                    </div>
                  </CDSCollapse.Panel>
                </CDSCollapse>
              </CDSFlex>
            </CDSCard>

            <CDSCard>
              <CDSFlex direction="vertical" gap={16}>
                <div>
                  <h2>Guided inputs</h2>
                  <p className="uag-section-copy">
                    Define the API contract shape before implementation exists.
                  </p>
                </div>

                <CDSTextInput
                  id="api-name"
                  label="API name (title)"
                  contextualHint="Example: Cisco Security Cloud Control (Platform Management APIs)"
                  placeholder="Cisco Security Cloud Control (Platform Management APIs)"
                  value={formState.apiName}
                  onChange={handleInputChange("apiName")}
                />
                <CDSTextInput
                  id="api-description"
                  label="Description"
                  placeholder="Example: Manage organizations, subscriptions, and access policies in Security Cloud Control."
                  value={formState.description}
                  onChange={handleInputChange("description")}
                />

                <div className="uag-design-two-up">
                  <CDSTextInput
                    id="api-version"
                    label="Version"
                    value={formState.version}
                    onChange={handleInputChange("version")}
                  />
                  <CDSTextInput
                    id="api-base-path"
                    label="Base path"
                    value={formState.basePath}
                    onChange={handleInputChange("basePath")}
                  />
                </div>

                <CDSTextInput
                  id="api-server"
                  label="Server URL"
                  value={formState.serverUrl}
                  onChange={handleInputChange("serverUrl")}
                />

                <div>
                  <CDSFlex direction="vertical" gap={8}>
                    <div>
                      <p className="uag-form-label">Primary resources (tags)</p>
                      <p className="uag-form-copy">
                        Add the resources the generated spec should expose. Each resource can carry
                        one or more paths with their own methods and operation details.
                      </p>
                    </div>
                    <CDSFlex gap={8} className="uag-resource-entry">
                      <CDSButton variant="secondary" onClick={openResourceModal}>
                        + Add
                      </CDSButton>
                    </CDSFlex>
                    <CDSFlex gap={8} wrap className="uag-resource-tags">
                      {resourceList.map((resource) => (
                        <div key={resource.name} className="uag-resource-tag">
                          <button
                            type="button"
                            className="uag-resource-tag-trigger"
                            onClick={() => openResourceModal(resource)}
                            aria-label={`Edit ${resource.name}`}
                            title="Edit"
                          >
                            <CDSTag>{resource.name}</CDSTag>
                          </button>
                          <button
                            type="button"
                            className="uag-resource-tag-remove"
                            aria-label={`Remove ${resource.name}`}
                            title="Delete"
                            onClick={() => handleRemoveResource(resource.name)}
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </CDSFlex>
                  </CDSFlex>
                </div>

              <CDSRadio
                legend="Authentication model"
                name="auth-model"
                value={formState.authModel}
                onChange={handleInputChange("authModel")}
                options={[
                  { key: "bearer", label: "Bearer token", value: "bearer" },
                  { key: "none", label: "No authentication", value: "none" },
                ]}
              />

              <CDSRadio
                legend="Pagination strategy"
                name="pagination-model"
                value={formState.paginationModel}
                onChange={handleInputChange("paginationModel")}
                options={[
                  { key: "cursor", label: "Cursor", value: "cursor" },
                  { key: "none", label: "No pagination", value: "none" },
                ]}
              />

                <div className="uag-design-two-up">
                  <CDSRadio
                    legend="Tracking headers"
                    name="tracking-headers"
                    value={formState.includeTrackingHeaders}
                    onChange={handleInputChange("includeTrackingHeaders")}
                    options={[
                      { key: "tracking-yes", label: "Include", value: "yes" },
                      { key: "tracking-no", label: "Skip", value: "no" },
                    ]}
                  />
                  <CDSRadio
                    legend="Error response model"
                    name="error-model"
                    value={formState.errorModel}
                    onChange={handleInputChange("errorModel")}
                    options={[
                      { key: "error-standard", label: "Reusable components", value: "standard" },
                      { key: "error-minimal", label: "Inline minimal", value: "minimal" },
                    ]}
                  />
                </div>

                <CDSFlex gap={8}>
                  <CDSButton variant="secondary" onClick={handleDownload}>
                    Download YAML
                  </CDSButton>
                  <CDSButton variant="secondary" onClick={handleReset}>
                    Clear
                  </CDSButton>
                </CDSFlex>
              </CDSFlex>
            </CDSCard>
          </CDSFlex>

          <CDSFlex direction="vertical" gap={16} className="uag-design-preview-column">
            <CDSCard className="uag-design-preview-card">
              <CDSFlex direction="vertical" gap={12}>
                <div>
                  <h2>Generated OpenAPI specification</h2>
                  <p className="uag-section-copy">
                    {aiGeneratedSpec
                      ? "Review the AI-generated draft or switch back to the manual builder preview."
                      : "Review the generated structure before export or review."}
                  </p>
                </div>
                <CDSFlex gap={8} wrap>
                  {aiGeneratedSpec ? (
                    <CDSTag.Colored color="accentD">AI generated</CDSTag.Colored>
                  ) : (
                    <CDSTag.Colored color="accentC">Manual build</CDSTag.Colored>
                  )}
                  {aiGeneratedSpec ? (
                    <CDSButton variant="tertiary" onClick={handleRemoveAiDraft}>
                      Remove AI draft
                    </CDSButton>
                  ) : null}
                </CDSFlex>
                <textarea
                  aria-label="Generated OpenAPI specification"
                  className="uag-spec-preview"
                  readOnly
                  spellCheck={false}
                  value={generatedSpec}
                />
              </CDSFlex>
            </CDSCard>

            <CDSContainer className="uag-design-summary">
              <CDSFlex direction="vertical" gap={16}>
                <p className="uag-section-copy">
                  Preview updates automatically. Reusable headers and standard
                  error responses are emitted under <code>components</code> when selected.
                </p>
                <CDSFlex gap={12} wrap>
                  <CDSTag.Colored color="accentB">
                    {resourceList.length} resources
                  </CDSTag.Colored>
                  <CDSTag.Colored color="accentC">
                    {tagList.length} tags
                  </CDSTag.Colored>
                  <CDSTag.Colored color="accentD">{authSummaryLabel}</CDSTag.Colored>
                  <CDSTag.Colored color="accentE">{paginationSummaryLabel}</CDSTag.Colored>
                  <CDSTag.Colored color="accentA">
                    {methodCount} methods
                  </CDSTag.Colored>
                </CDSFlex>
              </CDSFlex>
            </CDSContainer>
          </CDSFlex>
        </div>
      </CDSFlex>

      {isGeneratingSpec ? (
        <div className="uag-onboard-loading-overlay">
          <div className="uag-onboard-loading-panel">
            <div className="uag-onboard-spinner" aria-hidden="true" />
            <output className="uag-onboard-loading-status" aria-live="polite">
              Generating AI spec
            </output>
            <p>Generating the OpenAPI draft and syncing the form with the AI response.</p>
          </div>
        </div>
      ) : null}

      <CDSModal isOpen={isResourceModalOpen} size="lg" onClose={closeResourceModal}>
        <CDSFlex direction="vertical" gap={16} className="uag-resource-modal">
          <div>
            <h2>{editingResourceName ? "Edit primary resource" : "Add primary resource"}</h2>
            <p className="uag-section-copy">
              Start with a tag name, then add one or more paths. For each path, capture the route,
              summary, operation ID, optional description, and supported methods.
            </p>
          </div>

          <CDSTextInput
            id="resource-name"
            label="Name (tag name)"
            placeholder="Example: organizations"
            value={resourceDraft.name}
            onChange={handleResourceDraftChange("name")}
          />

          <div>
            <p className="uag-form-label">Paths</p>
            <p className="uag-form-copy">
              Add paths one by one. Click an existing path to edit it, or use delete if it no
              longer belongs to the resource.
            </p>
          </div>

          <CDSFlex gap={8} className="uag-resource-entry uag-resource-entry--modal">
            <CDSButton variant="secondary" onClick={() => openPathModal()}>
              + Add path
            </CDSButton>
          </CDSFlex>

          <CDSFlex gap={8} wrap className="uag-resource-tags">
            {resourceDraft.paths.map((entry) => (
              <div key={entry.path} className="uag-resource-tag">
                <button
                  type="button"
                  className="uag-resource-tag-trigger"
                  aria-label={`Edit ${entry.path}`}
                  title="Edit"
                  onClick={() => openPathModal(entry)}
                >
                  <CDSTag>{entry.path}</CDSTag>
                </button>
                <button
                  type="button"
                  className="uag-resource-tag-remove"
                  aria-label={`Remove ${entry.path}`}
                  title="Delete"
                  onClick={() => handleRemoveDraftPath(entry.path)}
                >
                  x
                </button>
              </div>
            ))}
          </CDSFlex>

          {resourceDraftError ? (
            <p className="uag-resource-error">{resourceDraftError}</p>
          ) : null}

          <CDSFlex gap={8}>
            <CDSButton variant="secondary" onClick={closeResourceModal}>
              Cancel
            </CDSButton>
            <CDSButton onClick={handleSaveResource}>Save resource</CDSButton>
          </CDSFlex>
        </CDSFlex>
      </CDSModal>

      <CDSModal isOpen={isPathModalOpen} size="lg" onClose={closePathModal}>
        <CDSFlex direction="vertical" gap={16} className="uag-resource-modal">
          <div>
            <h2>{editingPathValue ? "Edit path" : "Add path"}</h2>
            <p className="uag-section-copy">
              The sample spec shows that most OpenAPI boilerplate can be generated. Here you only
              need to define the path contract details that are specific to your API.
            </p>
          </div>

          <CDSTextInput
            id="path-value"
            label="Path"
            placeholder="Example: /organizations/{orgId}"
            value={pathDraft.path}
            onChange={handlePathDraftChange("path")}
          />
          <CDSTextInput
            id="path-summary"
            label="Summary"
            placeholder="Example: Get Organization Details"
            value={pathDraft.summary}
            onChange={handlePathDraftChange("summary")}
          />
          <CDSTextInput
            id="path-operation-id"
            label="Operation ID"
            placeholder="Example: organizationDetails"
            value={pathDraft.operationId}
            onChange={handlePathDraftChange("operationId")}
          />
          <CDSTextInput
            id="path-description"
            label="Description"
            placeholder="Optional details shown in the generated OpenAPI operation."
            value={pathDraft.description}
            onChange={handlePathDraftChange("description")}
          />

          <div>
            <p className="uag-form-label">Methods</p>
            <p className="uag-form-copy">
              Choose the methods this path should support. Responses, standard errors, tracking
              headers, and security blocks can be generated automatically.
            </p>
          </div>
          <CDSFlex direction="vertical" gap={8}>
            {methodOptions.map((method) => (
              <CDSCheckbox
                key={method}
                checked={pathDraft.methods.includes(method)}
                onChange={handleDraftMethodChange(method)}
              >
                {method.toUpperCase()}
              </CDSCheckbox>
            ))}
          </CDSFlex>

          {pathDraftError ? <p className="uag-resource-error">{pathDraftError}</p> : null}

          <CDSFlex gap={8}>
            <CDSButton variant="secondary" onClick={closePathModal}>
              Cancel
            </CDSButton>
            <CDSButton onClick={handleSavePathDraft}>Save path</CDSButton>
          </CDSFlex>
        </CDSFlex>
      </CDSModal>
    </section>
  );
};

export const __testables = {
  getPaginationParameterName,
  getSecuritySchemeName,
  normalizePathEntry,
  parseGeneratedSpecDocument,
  getServerFields,
  getAuthModelFromDocument,
  getFormStateFromGeneratedSpec,
  syncGeneratedSpecWithFormState,
  getStoredDesignState,
  getInitialDesignState,
  persistDesignState,
  parseDesignStorageEvent,
};

export default DesignApiPage;
